import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  RiCalendarCheckLine, RiTimeLine, RiArrowLeftLine,
  RiCheckLine, RiSparklingLine, RiScissorsFill,
} from 'react-icons/ri'
import { servicesAPI } from '../api'
import SEO from '../components/seo/SEO'
import { breadcrumb, service as serviceLd } from '../components/seo/jsonLd'

const FALLBACK = {
  'hair-coloring':     { name: 'Hair Coloring',     price: 2500, duration_minutes: 120, description: 'Full color, highlights, balayage and tone correction by master colourists. Customised to your skin tone, lifestyle and the look you want next.' },
  'haircut-style':     { name: 'Haircut & Style',   price: 800,  duration_minutes: 60,  description: 'Precision cuts and finishing styles tailored to your face shape, hair texture and routine. Includes a thorough consultation and blow-dry.' },
  'bridal-makeup':     { name: 'Bridal Makeup',     price: 8000, duration_minutes: 180, description: 'A complete bridal package — HD or airbrush makeup, bridal hairstyling, draping coordination and a complimentary trial. Designed to last every minute of your big day.' },
  'deep-conditioning': { name: 'Deep Conditioning', price: 1200, duration_minutes: 90,  description: 'Salon-grade hair masks and scalp therapy that restore moisture, repair breakage and bring back mirror-shine to dry, frizzy or chemically-treated hair.' },
  'facial':            { name: 'Facial Treatment',  price: 1500, duration_minutes: 60,  description: 'Customised facial designed for your skin type and concerns — cleanse, exfoliate, mask, massage and finish with active serums for a clean, glowing finish.' },
  'nail-art':          { name: 'Nail Art',          price: 600,  duration_minutes: 45,  description: 'Intricate hand-painted nail art and gel finishes by certified technicians. Pick from our look book or bring your own inspiration.' },
}

const INCLUSIONS = [
  'Detailed consultation with your stylist',
  'Premium salon-grade products',
  'Hot or cold beverage of your choice',
  'Sanitised, single-use tools where possible',
  'After-care guidance and product recommendations',
]

export default function ServiceDetail() {
  const { slug } = useParams()

  const { data, isLoading } = useQuery({
    queryKey: ['service', slug],
    queryFn: () => servicesAPI.getBySlug(slug).then((r) => r.data?.data || r.data),
    retry: false,
  })

  const service = data || FALLBACK[slug] || {
    name: slug?.split('-').map((w) => w[0]?.toUpperCase() + w.slice(1)).join(' ') || 'Service',
    price: 1500,
    duration_minutes: 60,
    description: 'A signature Salon Vora experience tailored to your needs by our expert team.',
  }

  return (
    <>
      <SEO
        title={`${service.name} in Chennai`}
        description={service.description?.slice(0, 160) || `Book ${service.name} at Salon Vora — premium service by master stylists.`}
        path={`/services/${slug}`}
        jsonLd={[
          breadcrumb([
            { name: 'Home',     url: '/' },
            { name: 'Services', url: '/services' },
            { name: service.name, url: `/services/${slug}` },
          ]),
          serviceLd({
            name: service.name,
            description: service.description,
            slug,
            price: service.price,
          }),
        ]}
      />

      <div className="min-h-screen bg-salon-cream">
        {/* Hero */}
        <section className="relative bg-hero-gradient py-32 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-salon-primary/30 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-40 -left-40 w-[400px] h-[400px] rounded-full bg-salon-secondary/15 blur-3xl pointer-events-none" />

          <div className="container-salon relative z-10 max-w-4xl">
            <Link to="/services" className="inline-flex items-center gap-1 text-salon-secondary text-sm font-body mb-6 hover:underline">
              <RiArrowLeftLine /> Back to all services
            </Link>

            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-salon-secondary px-3 py-1 rounded-full bg-salon-secondary/10 mb-4">
              <RiSparklingLine className="inline mr-1" /> Salon Vora Service
            </span>

            <h1 className="font-display text-white text-4xl md:text-6xl font-bold mb-5 leading-tight">
              {service.name}
            </h1>

            <p className="text-white/80 font-body text-lg leading-relaxed max-w-2xl mb-8">
              {service.description}
            </p>

            <div className="flex flex-wrap items-center gap-4">
              <Link to={`/book?service=${service.id || slug}`} className="btn-gold text-base px-8 py-4 shadow-gold">
                <RiCalendarCheckLine className="text-xl" /> Book This Service
              </Link>
              <div className="flex items-center gap-4 text-white/80 font-body">
                <span className="inline-flex items-center gap-1.5"><RiTimeLine /> {service.duration_minutes || 60} min</span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-salon-secondary font-display font-bold text-2xl">
                    ₹{Number(service.price || 0).toLocaleString()}
                  </span>
                  <span className="text-white/50 text-sm">starting from</span>
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* What's included */}
        <section className="py-20">
          <div className="container-salon max-w-4xl">
            <div className="grid md:grid-cols-2 gap-12 items-start">
              <div>
                <span className="section-tag">What's included</span>
                <h2 className="section-title mt-2 mb-4">Every booking, every time.</h2>
                <p className="text-salon-muted font-body leading-relaxed">
                  Whatever service you choose, the Salon Vora standard never changes. Here's what you can expect from the moment you walk in.
                </p>
              </div>
              <ul className="space-y-3">
                {INCLUSIONS.map((it) => (
                  <li key={it} className="flex items-start gap-3 bg-white border border-salon-cream-dark rounded-2xl p-4">
                    <span className="w-7 h-7 rounded-full bg-salon-primary/10 flex items-center justify-center shrink-0">
                      <RiCheckLine className="text-salon-primary" />
                    </span>
                    <span className="text-salon-dark font-body">{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-white">
          <div className="container-salon">
            <div className="bg-salon-primary rounded-3xl px-8 py-14 text-center relative overflow-hidden">
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white/5 blur-3xl" />
                <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-salon-secondary/20 blur-3xl" />
              </div>
              <div className="relative z-10">
                <RiScissorsFill className="text-salon-secondary text-4xl mx-auto mb-4" />
                <h2 className="font-display text-white text-3xl md:text-4xl font-bold mb-3">
                  Ready for {service.name.toLowerCase()}?
                </h2>
                <p className="text-white/80 font-body mb-8 max-w-lg mx-auto">
                  Slots fill up fast. Lock in your preferred time and stylist now.
                </p>
                <Link to={`/book?service=${service.id || slug}`} className="btn-gold text-base px-10 py-4">
                  <RiCalendarCheckLine className="text-xl" /> Book Now
                </Link>
              </div>
            </div>
          </div>
        </section>

        {isLoading && null}
      </div>
    </>
  )
}
